import React from 'react';

import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import {JSONArray, JSONObject, JSONValue} from 'util/Types';

import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  CUSTOM_COMPONENT_KEY,
  getFieldSettingsIfPresent,
  isFieldUISchemaWithCustomComponent,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  ShapeUISchema,
} from '../StepConfigEditorTypes';
import AddArrayElementIcon from './AddArrayElementIcon';
import getSortedSubfields from './getSortedSubfields';
import StepConfigEditorRouter from './StepConfigEditorRouter';
import StepConfigFieldsTemplate from './StepConfigFieldsTemplate';

interface ShapeEditorProps {
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'shape'>;
  onChangeConfig: (config: JSONValue) => void;
  disabled?: boolean;
  uiSchema?: ShapeUISchema;
  /**
   * If `true` - the shape will be rendered with two fields per row
   * If `false` - the shape will be rendered with one field per row
   */
  isTopLevel?: boolean;
  /**
   * Forces single column for all fields, even if top level is `true`
   */
  forceSingleColumnDisplay?: boolean;
}

/**
 * @deprecated ShapeEditor will be replaced by ShapeEditorV2 once it reaches full parity.
 * TODO(BNCH-121564): Remove this file after ShapeEditorV2 is fully launched
 * @package
 * */
export default function ShapeEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
  uiSchema,
  isTopLevel = false,
  forceSingleColumnDisplay = false,
}: ShapeEditorProps): JSX.Element {
  const {inputDataFrames} = useStepConfigEditorContext();
  function handleChangeConfig(newConfig: JSONObject): void {
    if (uiSchema?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      onChangeConfig(uiSchema[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](newConfig, config, inputDataFrames));
    } else {
      onChangeConfig(newConfig);
    }
  }

  const fields = getSortedSubfields(configShape.fields, uiSchema).map(([variableName, variableShape]) => {
    const field = uiSchema?.[variableName];
    const addIcon =
      variableShape.kind === 'array' &&
      // Arrays of column references use a multi-select dropdown, so we don't need to add multiple editors
      variableShape.elementSpec.kind !== 'columnReference' &&
      !disabled &&
      !isFieldUISchemaWithCustomComponent(field) ? (
        <AddArrayElementIcon
          specShape={variableShape}
          config={config[variableName] as JSONArray | null}
          onChangeConfig={(newValue) => {
            handleChangeConfig({...config, [variableName]: newValue});
          }}
          elementUiSchema={getFieldSettingsIfPresent(uiSchema, variableName)}
          dataTestAttrsKey={variableName}
        />
      ) : undefined;
    return {
      variableName: field?.displayName ?? variableName,
      infoText: field?.infoText,
      addIcon,
      editor: isFieldUISchemaWithCustomComponent(field) ? (
        field[CUSTOM_COMPONENT_KEY]({
          configShape: configShape.fields[variableName],
          config: config[variableName],
          onChangeConfig: (newConfig) => {
            if (field[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
              handleChangeConfig({
                ...config,
                [variableName]: field[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](
                  newConfig,
                  config[variableName],
                  inputDataFrames
                ),
              });
            } else {
              handleChangeConfig({...config, [variableName]: newConfig});
            }
          },
          disabled,
        })
      ) : (
        <StepConfigEditorRouter
          config={config[variableName]}
          configShape={variableShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={(newConfig) => {
            handleChangeConfig({...config, [variableName]: newConfig});
          }}
          disabled={disabled}
          uiSchema={getFieldSettingsIfPresent(uiSchema, variableName)}
        />
      ),
    };
  });

  return (
    <StepConfigFieldsTemplate numColumns={isTopLevel || forceSingleColumnDisplay ? 1 : 2} fields={fields} />
  );
}
